"use client";

// Modular columns for the Trades Summary table.
//
// Each column is a self-contained unit: it owns its header, its sort key, its
// "is this hidden when the pane is collapsed" flag, and how it renders a cell
// for a given row. The table shell decides which rows exist and what kind each
// row is; the row carries the small amount of data the column needs, and the
// column decides what to draw.
//
// See ONE_RING.md for the full column-by-column spec this is built from.
// Converted so far: Date, Description (fully unified — one render() that
// knows every row kind, including VS spreads), Days, Qty, Price, Last,
// Entry, Exit, MktVal, Income, Growth, Profit, plus the VS metric columns
// P(ITM)/eP(ITM)/HV%/IV-HV/Prem%. Same shape. (Money columns:
// page derives the number, the column owns the formatting — see note at
// makeMoneyColumn. MktVal/Income/Growth/Profit: minus on negatives only,
// color-coded; no "+" on positives.)

import React from "react";
import { fmtOpt, fmtMCap, fmtADV, $n, $d } from "@/lib/format";
import { ChartIcon, Exit, Refresh, XCircle } from "@/lib/icons";
import { useAuth } from "@/lib/auth-context";

// ---------------------------------------------------------------------------
// Canonical cell/header styling.
//
// Every column exports `th()` and `cell(code, …)` methods that bake in
// alignment + font + tone + padding. Callers never assemble
// `<td className="py-1 pr-4 text-right font-mono">{kindTone(code, …)}</td>`
// by hand — the failure mode (forgetting an arg, picking the wrong align)
// is structurally impossible. Per-row overrides go via `CellOpts`.
// ---------------------------------------------------------------------------

const CELL_BASE = "py-1 pr-4";
const TH_BASE = "py-1 pr-4 underline";

export type ColAlign = "left" | "center" | "right";

/** Per-cell overrides. The column owns canonical alignment/font/tone/padding;
 *  these knobs are for the row-level oddities (tooltip, colSpan, indent,
 *  extra className, click handlers). Every column's `cell(...)` accepts this
 *  as the last arg. */
export interface CellOpts {
  className?: string;
  title?: string;
  colSpan?: number;
  onClick?: (e: React.MouseEvent<HTMLTableCellElement>) => void;
  /** Indent the cell (child rows). Adds `pl-5`. */
  indent?: boolean;
  /** Replace the canonical `py-1 pr-4` padding (and align/font still apply).
   *  Use sparingly — only when a specific table has a unique density (e.g.
   *  the scan-results table uses `py-1 pr-[15px]`). */
  pad?: string;
}

const alignClass = (a: ColAlign): string =>
  a === "left" ? "text-left" : a === "right" ? "text-right" : "text-center";

function makeTh(header: React.ReactNode, align: ColAlign): React.ReactNode {
  return <th className={`${TH_BASE} ${alignClass(align)}`}>{header}</th>;
}

function makeTd(
  content: React.ReactNode,
  align: ColAlign,
  mono: boolean,
  opts?: CellOpts,
): React.ReactNode {
  const parts = [opts?.pad ?? CELL_BASE, alignClass(align)];
  if (mono) parts.push("font-mono");
  if (opts?.indent) parts.push("pl-5");
  if (opts?.className) parts.push(opts.className);
  return (
    <td className={parts.join(" ")} title={opts?.title} colSpan={opts?.colSpan} onClick={opts?.onClick}>
      {content}
    </td>
  );
}

/** Header padding override, mirrors CellOpts.pad. */
function makeThWithPad(header: React.ReactNode, align: ColAlign, pad: string): React.ReactNode {
  return <th className={`${pad} underline ${alignClass(align)}`}>{header}</th>;
}

/**
 * Open/closed tone owned by the column (not the caller). Pass `dim` =
 * true for a closed leg (muted) / false for open (foreground). Omit it
 * (undefined) to skip the wrap entirely — used when the caller is building
 * a precomputed node and will apply its own coloration later.
 */
export function dimWrap(
  node: React.ReactNode,
  dim?: boolean,
): React.ReactNode {
  if (dim === undefined) return node;
  return (
    <span className={dim ? "text-muted" : "text-foreground"}>{node}</span>
  );
}

/**
 * What a row is. The table shell tags each row with one of these so a column
 * can render itself differently per kind (e.g. a parent row aggregates, a
 * closed child shows a date range).
 */
export type RowKind =
  | "equity-detail" // top-level stock / option position row
  | "group-parent" // manual group parent (stock with children) — aggregate
  | "autogroup-parent" // standalone options grouped by underlying — aggregate
  | "child-open" // open option leg under a group
  | "child-closed" // closed option leg under a group
  | "autogroup-child"; // open option under an autogroup parent

// ---- Date formatting (shared, pure) ---------------------------------------

/**
 * Callers mix full timestamps (`openedAt`) with date-only values (`openedDate`
 * from the analysis endpoints, a Rust `NaiveDate`). `new Date()` reads a bare
 * `YYYY-MM-DD` as UTC midnight, which the local-time getters below then render
 * as the previous day anywhere west of Greenwich. Pin those to local midnight;
 * leave timestamps, which carry their own offset, alone.
 */
function parseISO(iso: string): Date {
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00` : iso);
}

/** `m/d/yy` — empty string when the date is missing. */
export function fmtMDY(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = parseISO(iso);
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
}

const WEEKDAY_ABBR = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/** Abbreviated weekday for a date-only ISO (`YYYY-MM-DD`) or timestamp. */
export function fmtWeekdayAbbr(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = parseISO(iso);
  if (Number.isNaN(d.getTime())) return "";
  return WEEKDAY_ABBR[d.getDay()];
}

/** `m/d/yy Mon` — empty string when the date is missing. */
export function fmtMDYWeekday(iso: string | null | undefined): string {
  const m = fmtMDY(iso);
  if (!m) return "";
  const wd = fmtWeekdayAbbr(iso);
  return wd ? `${m} ${wd}` : m;
}

/**
 * Opened–closed range used by closed legs. Year shown once when both ends
 * share it, otherwise on each end.
 */
export function fmtDateRange(
  startISO: string,
  endISO: string | null | undefined,
): string {
  const ed = parseISO(startISO);
  // An open leg has no close date yet — show "opened–today".
  const ld = endISO ? parseISO(endISO) : new Date();
  const oy = String(ed.getFullYear()).slice(-2);
  const cy = String(ld.getFullYear()).slice(-2);
  const oF = `${ed.getMonth() + 1}/${ed.getDate()}`;
  const cF = `${ld.getMonth() + 1}/${ld.getDate()}`;
  return oy === cy ? `${oF}-${cF}/${cy}` : `${oF}/${oy}-${cF}/${cy}`;
}

/** Close fill clock as `hh:mm` in America/New_York, or "" if date-only / missing. */
function fmtCloseHHMM(closeTimeISO?: string | null): string {
  if (!closeTimeISO || /^\d{4}-\d{2}-\d{2}$/.test(closeTimeISO)) return "";
  const d = new Date(closeTimeISO);
  if (Number.isNaN(d.getTime())) return "";
  // Convert via ET wall-clock components (same pattern as ageDte / trading-days).
  const et = new Date(d.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const hh = String(et.getHours()).padStart(2, "0");
  const mm = String(et.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

/**
 * Per-trade detail Date/Time (multi-day, e.g. VS): `m/d-m/d hh:mm` (no year).
 * Time is the trade's close fill in America/New_York.
 */
export function fmtDateRangeCloseTime(
  startISO: string,
  endISO: string | null | undefined,
  closeTimeISO?: string | null,
): string {
  const ed = parseISO(startISO);
  const ld = endISO ? parseISO(endISO) : new Date();
  const range = `${ed.getMonth() + 1}/${ed.getDate()}-${ld.getMonth() + 1}/${ld.getDate()}`;
  const tSrc = (closeTimeISO && closeTimeISO.length > 0) ? closeTimeISO : endISO;
  const hm = fmtCloseHHMM(tSrc);
  return hm ? `${range} ${hm}` : range;
}

/**
 * Per-trade detail Date/Time for same-day trades (0DTE): `m/d hh:mm`.
 * Date from the close day; time is the close fill in America/New_York.
 */
export function fmtDateCloseTime(
  dateISO: string | null | undefined,
  closeTimeISO?: string | null,
): string {
  const src = dateISO || closeTimeISO;
  if (!src) return "";
  const d = parseISO(src);
  const md = `${d.getMonth() + 1}/${d.getDate()}`;
  const tSrc = (closeTimeISO && closeTimeISO.length > 0) ? closeTimeISO : dateISO;
  const hm = fmtCloseHHMM(tSrc);
  return hm ? `${md} ${hm}` : md;
}

// ---- Date column ----------------------------------------------------------

/** The only data the Date column needs from a row. */
export interface DateCellInput {
  /** Single ISO date (most rows). Ignored when `range` is set. */
  iso?: string | null;
  /**
   * Closed legs show an opened–closed range instead of a single date. A
   * null/undefined `end` (an open leg) renders as "opened–today".
   */
  range?: { start: string; end: string | null | undefined } | null;
  /** Indent the cell (child rows, and a stock parent's own row). */
  indent?: boolean;
  /** Placeholder when a single date is missing. Default "" (Trades Summary);
   * Vertical Spreads passes "—". */
  emptyText?: string;
}

export const dateColumn = {
  key: "date",
  header: "Dates",
  sortKey: "date",
  detail: false,



  /**
   * Just the formatted text for a cell. Used while the table still owns the
   * surrounding `<td>` wrapper; once every column is converted, `cell()`
   * below takes over wrapper ownership too.
   */
  value(input: DateCellInput, dim?: boolean): React.ReactNode {
    let t: string;
    if (input.range) t = fmtDateRange(input.range.start, input.range.end);
    else {
      const m = fmtMDY(input.iso);
      t = m === "" ? input.emptyText ?? "" : m;
    }
    return dimWrap(t, dim);
  },

  /** Canonical header — left-aligned, underlined. */
  th(): React.ReactNode {
    return makeTh(this.header, "left");
  },

  /** Canonical cell — left-aligned, muted text (dates always muted). */
  cell(input: DateCellInput, opts?: CellOpts) {
    return makeTd(this.value(input), "left", false, { ...opts, className: `text-muted${opts?.className ? " " + opts.className : ""}`, indent: opts?.indent ?? input.indent });
  },
};

// ---- Entry Date column -----------------------------------------------------

/**
 * The trade's entry (open) date only — a single M/D/YY, no opened–closed
 * range. Tone is applied by the caller via `kindTone`.
 */
export const entryDateColumn = {
  key: "entrydate",
  header: "Date",
  th(): React.ReactNode { return makeTh("Date", "center"); },
  render(openedISO: string | null | undefined): React.ReactNode {
    const m = fmtMDY(openedISO);
    return m === "" ? "—" : m;
  },
  cell(code: string, openedISO: string | null | undefined, opts?: CellOpts): React.ReactNode {
    return makeTd(kindTone(code, this.render(openedISO)), "center", false, opts);
  },
};

// ---- Order Age column ------------------------------------------------------

/** Whole minutes between an ISO timestamp and `nowMs` (floored at 0). */
export function minutesSince(iso: string | null | undefined, nowMs: number): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((nowMs - t) / 60_000));
}

/**
 * How long ago a working order was entered: `12m`, `1h 05m`, `2d 3h`.
 * The page passes `nowMs` from `useMinuteTick()` so the cell re-renders
 * every minute. Tone is applied by the caller via `kindTone`.
 */
export const orderAgeColumn = {
  key: "age",
  header: "Age",
  th(): React.ReactNode { return makeTh("Age", "center"); },
  render(enteredISO: string | null | undefined, nowMs: number): React.ReactNode {
    const m = minutesSince(enteredISO, nowMs);
    if (m == null) return "—";
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ${String(m % 60).padStart(2, "0")}m`;
    return `${Math.floor(h / 24)}d ${h % 24}h`;
  },
  cell(code: string, enteredISO: string | null | undefined, nowMs: number, opts?: CellOpts): React.ReactNode {
    return makeTd(kindTone(code, this.render(enteredISO, nowMs)), "center", true, opts);
  },
};

// ---- Exp Date column -------------------------------------------------------

/**
 * The option's expiration date as `M/D/YY Day` (e.g. `7/28/26 Mon`). Tone is
 * applied by the caller via `kindTone`.
 */
export const expDateColumn = {
  key: "expdate",
  header: "Exp Date",
  th(): React.ReactNode { return makeTh("Exp Date", "center"); },
  render(expirationISO: string | null | undefined): React.ReactNode {
    const m = fmtMDYWeekday(expirationISO);
    return m === "" ? "—" : m;
  },
  cell(code: string, expirationISO: string | null | undefined, opts?: CellOpts): React.ReactNode {
    return makeTd(kindTone(code, this.render(expirationISO)), "center", false, opts);
  },
};

// ---- Symbol column ---------------------------------------------------------

/**
 * Underlying ticker plus a chart-graph icon button that opens a price graph.
 * The page owns the chart modal; it passes an `onChart(sym)` opener. Tone is
 * applied by the caller via `kindTone` (open → 100% white, closed → dim);
 * the icon keeps its own muted/cyan hover styling regardless.
 */
export const symbolColumn = {
  key: "symbol",
  header: "Symbol",


  /** Canonical header — left-aligned, underlined. */
  th(): React.ReactNode {
    return makeTh(this.header, "left");
  },

  /** Underlying ticker plus a chart-graph icon. `onChart` is optional: when
   *  omitted the icon is suppressed entirely. */
  render(underlying: string, onChart?: (sym: string) => void): React.ReactNode {
    return (
      <>
        <span>{underlying}</span>
        {onChart != null && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onChart(underlying);
            }}
            className="inline-flex items-center justify-center p-0 m-0 leading-none align-middle ml-2 text-muted hover:text-cyan"
            title="Chart"
          >
            <ChartIcon />
          </button>
        )}
      </>
    );
  },

  /** Canonical cell — left-aligned, code-driven tone, icon when onChart given. */
  cell(code: string, underlying: string, onChart?: (sym: string) => void, opts?: CellOpts): React.ReactNode {
    return makeTd(kindTone(code, this.render(underlying, onChart)), "left", false, opts);
  },
};

// ---- Age/DTE column --------------------------------------------------------

/**
 * Today's ET trading-day date, as a UTC-midnight epoch. Both day counters below
 * anchor to it: comparing midnight-of-expiration to wall-clock `Date.now()`
 * would round DTE down to 0 anytime after midnight ET the day before expiry.
 */
function etTodayDay(): number {
  const nowET = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" }));
  return Date.UTC(nowET.getFullYear(), nowET.getMonth(), nowET.getDate());
}

/** Whole days to expiration, floored at 0. */
export function dteFrom(expirationISO: string): number {
  const [yyyy, mm, dd] = expirationISO.slice(0, 10).split("-").map(Number);
  return Math.max(0, Math.round((Date.UTC(yyyy, mm - 1, dd) - etTodayDay()) / 86400000));
}

/** Whole days since entry and whole days to expiration, both floored at 0. */
export function ageDte(openedISO: string, expirationISO: string): { age: number; dte: number } {
  const openET = new Date(new Date(openedISO).toLocaleString("en-US", { timeZone: "America/New_York" }));
  const opDay = Date.UTC(openET.getFullYear(), openET.getMonth(), openET.getDate());
  return {
    age: Math.max(0, Math.round((etTodayDay() - opDay) / 86400000)),
    dte: dteFrom(expirationISO),
  };
}

/**
 * Combined "Age / DTE" for an open spread: Age = whole days since entry,
 * DTE = whole days to expiration (floored at 0). Rendered as `age / dte`.
 * Pure 0DTE (age=0 and dte=0) renders bold "0DTE" instead of "0 / 0" so it
 * stands out next to multi-day spreads on the Vertical Spreads page.
 * Tone is applied by the caller via `kindTone` (open → 100% white, closed → dim).
 */
export const ageDteColumn = {
  key: "agedte",
  header: "Age/DTE",


  th(): React.ReactNode {
    return (
      <th className={`${TH_BASE} text-center align-bottom leading-tight`}>
        Age/<br />DTE
      </th>
    );
  },

  cell(code: string, openedISO: string, expirationISO: string, opts?: CellOpts): React.ReactNode {
    return makeTd(kindTone(code, this.render(openedISO, expirationISO)), "center", false, opts);
  },

  render(openedISO: string, expirationISO: string): React.ReactNode {
    const { age, dte } = ageDte(openedISO, expirationISO);
    if (age === 0 && dte === 0) {
      return <span className="font-bold">0DTE</span>;
    }
    return `${age} / ${dte}`;
  },
};

/**
 * DTE alone. Open Orders rows have no position age to report — a working order
 * may not correspond to a held position at all — so they show only days to
 * expiration.
 */
export const dteColumn = {
  key: "dte",
  header: "DTE",

  th(): React.ReactNode { return makeTh("DTE", "center"); },

  render(expirationISO: string | null | undefined): React.ReactNode {
    return expirationISO ? String(dteFrom(expirationISO)) : "—";
  },

  cell(code: string, expirationISO: string | null | undefined, opts?: CellOpts): React.ReactNode {
    return makeTd(kindTone(code, this.render(expirationISO)), "center", false, opts);
  },
};

// ---- Bid/Ask/Mid/Last column (expandable) ----------------------------------

/** Quote inputs for the B/A/M/L column. `price` is the entry reference
 * (per-share net premium) the mid/last are compared against. */
export interface BamlInput {
  bid: number | null;
  ask: number | null;
  last: number | null;
  price: number | null;
}

/**
 * A self-expanding quote column. Collapsed: header "Mid", body the
 * mid = (bid+ask)/2. Expanded: header "Bid / Ask / Mid / Last", body all
 * four. Mid & Last are green when below the entry price, red when above,
 * white when equal; Bid & Ask are always white. Intensity follows the row
 * code (open → 100%, closed → dim) like every other ledger column. The page
 * owns the expand state and passes it + a toggle into the header.
 */
export const bamlColumn = {
  key: "baml",

  /** Canonical header — center, clickable to toggle expanded. */
  th(expanded: boolean, onToggle: () => void): React.ReactNode {
    return (
      <th
        className={`${TH_BASE} text-center cursor-pointer select-none`}
        onClick={onToggle}
        title={expanded ? "Collapse" : "Expand"}
      >
        {expanded ? "Bid / Ask / Mid / Last ▴" : "Mid ▾"}
      </th>
    );
  },

  /** Canonical cell — center, font-mono. */
  cell(code: string, input: BamlInput, expanded: boolean, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(code, input, expanded), "center", true, opts);
  },

  render(code: string, input: BamlInput, expanded: boolean): React.ReactNode {
    const dim = kindDim(code);
    const mid =
      input.bid != null && input.ask != null
        ? (input.bid + input.ask) / 2
        : null;
    const cmp = (v: number | null): "green" | "red" | "white" =>
      v == null || input.price == null
        ? "white"
        : v > input.price
          ? "red"
          : v < input.price
            ? "green"
            : "white";
    const cell = (v: number | null, tone: "green" | "red" | "white") => (
      <span className={toneClass(tone, dim)}>
        {v != null ? v.toFixed(2) : "—"}
      </span>
    );
    if (!expanded) return cell(mid, cmp(mid));
    const sep = <span className="text-muted"> / </span>;
    return (
      <span>
        {cell(input.bid, "white")}
        {sep}
        {cell(input.ask, "white")}
        {sep}
        {cell(mid, cmp(mid))}
        {sep}
        {cell(input.last, cmp(input.last))}
      </span>
    );
  },
};

// ---- Strikes column --------------------------------------------------------

/**
 * Short/long strikes for a spread, e.g. `5000/4990`. White normally; red
 * when the underlying is below the short strike. Intensity follows the row
 * code (open → 100%, closed → dim) like every other ledger column.
 */
// Credit-spread codes are C[C|P]S[O|N|X|C|P]: slot 0 = C, slot 1 = the option
// right (C=call, P=put), slot 2 = S. A bear-CALL spread is at risk (ITM) when
// the underlying is ABOVE the short strike; a bull-PUT spread when BELOW.
// Covered-call codes (CCO/CCC/CCN — no 'S' in slot 2) are not spreads and keep
// the default put-style orientation.
function isCallSpreadCode(code: string): boolean {
  const c = code.trim();
  return c[0] === "C" && c[1] === "C" && c[2] === "S";
}

export const strikesColumn = {
  key: "strikes",
  header: "Strikes",


  th(): React.ReactNode { return makeTh("Strikes", "center"); },

  cell(
    code: string,
    shortStrike: number,
    longStrike: number,
    underlying: number | null | undefined,
    shortSymbol?: string | null,
    longSymbol?: string | null,
    opts?: CellOpts,
  ): React.ReactNode {
    return makeTd(this.render(code, shortStrike, longStrike, underlying, shortSymbol, longSymbol), "center", true, opts);
  },

  render(
    code: string,
    shortStrike: number,
    longStrike: number,
    underlying: number | null | undefined,
    shortSymbol?: string | null,
    longSymbol?: string | null,
  ): React.ReactNode {
    const k = (v: number) => (v % 1 === 0 ? v.toFixed(0) : v.toFixed(1));
    const dim = kindDim(code);
    // Short leg goes red as the underlying threatens it: call spreads when
    // price rises ABOVE the short strike, put spreads when it falls BELOW.
    const breached = underlying != null &&
      (isCallSpreadCode(code) ? underlying > shortStrike : underlying < shortStrike);
    const shortTone: "red" | "white" = breached ? "red" : "white";
    // Tooltip: the actual OCC symbols of the two legs. The leg roots can
    // differ (e.g. RUTW weekly vs RUT monthly) for the same spread, so show
    // them verbatim — collapsing only the OCC's internal padding spaces.
    const tidy = (s: string) => s.replace(/\s+/g, " ").trim();
    const title =
      shortSymbol && longSymbol
        ? `Short: ${tidy(shortSymbol)}\nLong: ${tidy(longSymbol)}`
        : undefined;
    return (
      <span title={title} className={title ? "cursor-help" : undefined}>
        <span className={toneClass(shortTone, dim)}>{k(shortStrike)}</span>
        <span className={toneClass("white", dim)}>/{k(longStrike)}</span>
      </span>
    );
  },
};

// ---- Underlying column -----------------------------------------------------

/**
 * The underlying's price: current value for an open spread, value at exit
 * for a closed one (the caller passes whichever applies). Colored by whether
 * the underlying sits on the safe or breached side of the short strike:
 * put spreads are safe ABOVE the short (green) / breached below (red); call
 * spreads are the mirror — safe BELOW (green) / breached above (red). White
 * when equal. Intensity follows the row code (open → 100%, closed → dim).
 */
export const underlyingColumn = {
  key: "underlying",
  header: "Undl",


  th(): React.ReactNode { return makeTh("Undl", "center"); },

  cell(code: string, underlying: number | null | undefined, shortStrike: number, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(code, underlying, shortStrike), "center", true, opts);
  },

  render(
    code: string,
    underlying: number | null | undefined,
    shortStrike: number,
  ): React.ReactNode {
    if (underlying == null) {
      return <span className={toneClass("white", kindDim(code))}>—</span>;
    }
    // Green = underlying on the safe side of the short strike, red = breached.
    // Call spreads: safe below / breached above. Put spreads: the reverse.
    const safeAbove = !isCallSpreadCode(code);
    const tone: "green" | "red" | "white" =
      underlying === shortStrike
        ? "white"
        : (underlying > shortStrike) === safeAbove
          ? "green"
          : "red";
    return (
      <span className={toneClass(tone, kindDim(code))}>{$n(underlying)}</span>
    );
  },
};

// ---- Premium column --------------------------------------------------------

/**
 * Collected premium from the entry trades (total dollars). Always green;
 * intensity follows the row code (open → 100%, closed → dim).
 */
export const premiumColumn = {
  key: "premium",
  header: "Premium",


  th(): React.ReactNode { return makeTh("Premium", "center"); },

  cell(code: string, value: number | null | undefined, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(code, value), "center", true, opts);
  },

  render(code: string, value: number | null | undefined): React.ReactNode {
    if (value == null) return "";
    return (
      <span className={toneClass("green", kindDim(code))}>{$d(value)}</span>
    );
  },
};

// ---- Target column ---------------------------------------------------------

/**
 * Target exit price for an open spread, as defined by the Vertical Spread
 * prefs (tiered take-profit), or a live close order's price when one exists.
 * The page computes the number (see `calcBuybackTarget`); this column owns
 * the formatting. Tone is applied by the caller via `kindTone`.
 */
export const targetColumn = {
  key: "target",
  header: "Target",


  th(): React.ReactNode { return makeTh("Target", "center"); },

  render(target: number | null | undefined): React.ReactNode {
    return target != null ? $n(target) : "—";
  },

  cell(code: string, target: number | null | undefined, opts?: CellOpts): React.ReactNode {
    return makeTd(kindTone(code, this.render(target)), "center", true, opts);
  },
};

// ---- Exit Order column -----------------------------------------------------

export interface ExitOrderProps {
  /** Working close-order limit price; null/undefined when no order is entered. */
  limitPrice: number | null | undefined;
  duration?: string;
  onReplace?: () => void;
  onCancel?: () => void;
  cancelling?: boolean;
  /** Computed take-profit target — shown in place of the limit price when no
   *  close order is yet entered. */
  targetPrice?: number | null;
  /** Open the close-spread ticket seeded at the take-profit target — enter a
   *  stop. Shown only when no working close order is on the row.
   *  Null/undefined disables the green door icon. */
  onExit?: () => void;
  /** Close the position at the current mid. Shown on every open position. */
  onExitAtMid?: () => void;
}

/**
 * Exit Order: the working close order's limit price plus inline Replace /
 * Cancel icon buttons. No working order → green door (take-profit). Every
 * row also gets a red door (abandon at mid). Cell click handlers
 * stopPropagation so the row's own click isn't triggered.
 */
export const exitOrderColumn = {
  key: "exitOrder",
  header: "Exit Order",


  th(): React.ReactNode { return makeTh("Exit Order", "center"); },

  cell(props: ExitOrderProps, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(props), "center", false, opts);
  },

  render(props: {
    /** Working close-order limit price; null/undefined when no order is entered. */
    limitPrice: number | null | undefined;
    duration?: string;
    onReplace?: () => void;
    onCancel?: () => void;
    cancelling?: boolean;
    /** Computed take-profit target — shown in place of the limit price when
     *  no close order is yet entered. */
    targetPrice?: number | null;
    /** Open the close-spread ticket seeded at the take-profit — enter a stop.
     *  Null/undefined disables the green door icon. */
    onExit?: () => void;
    /** Close the position at the current mid. */
    onExitAtMid?: () => void;
  }): React.ReactNode {
    const hasOrder = props.limitPrice != null;
    // Working order → show its actual limit price (the price you're trying
    // to close at). No order yet → show the computed take-profit target so
    // the trader sees what the bot would place.
    const price = hasOrder ? props.limitPrice : (props.targetPrice ?? null);
    if (price == null && !hasOrder && !props.onExit && !props.onExitAtMid) return "—";
    const durationLabel = hasOrder && props.duration
      ? (props.duration === "GOOD_TILL_CANCEL" ? "GTC" : props.duration === "DAY" ? "DAY" : props.duration)
      : "------";
    return (
      <span className="inline-flex items-center gap-1.5 font-mono">
        <span className={hasOrder ? undefined : "text-muted"}>{price != null ? $n(price) : "—"}</span>
        <span className="text-[10px] text-muted uppercase tracking-wide">{durationLabel}</span>
        {hasOrder ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); props.onReplace?.(); }}
            className="inline-flex items-center justify-center p-0 m-0 leading-none text-cyan hover:text-cyan/80 transition-colors"
            title="Replace exit order"
            aria-label="Replace exit order"
          >
            <Refresh className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); props.onExit?.(); }}
            disabled={!props.onExit}
            className="inline-flex items-center justify-center p-0 m-0 leading-none text-green hover:text-green/80 disabled:opacity-40 transition-colors"
            title="Take Profit"
            aria-label="Take Profit"
          >
            <Exit className="w-3.5 h-3.5" />
          </button>
        )}
        {hasOrder ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); props.onCancel?.(); }}
            disabled={props.cancelling}
            className="inline-flex items-center justify-center p-0 m-0 leading-none text-red hover:text-red/80 disabled:opacity-40 transition-colors"
            title="Cancel exit order"
            aria-label="Cancel exit order"
          >
            <XCircle className="w-4 h-4" />
          </button>
        ) : (
          // Reserve the Cancel-slot width so columns line up row-to-row.
          <span className="inline-block w-4 h-4" aria-hidden="true" />
        )}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); props.onExitAtMid?.(); }}
          disabled={!props.onExitAtMid}
          className="inline-flex items-center justify-center p-0 m-0 leading-none text-red hover:text-red/80 disabled:opacity-40 transition-colors"
          title="Abandon"
          aria-label="Abandon"
        >
          <Exit className="w-3.5 h-3.5" />
        </button>
      </span>
    );
  },
};

// ---- Type column -----------------------------------------------------------

/**
 * Credit-spread kind by directional name: a put credit spread is a "Bull Put"
 * (isPut), a call credit spread is a "Bear Call". Tone is applied by the
 * caller via `kindTone` (open → 100% white, closed → dim white).
 */
export const typeColumn = {
  key: "type",
  header: "Type",


  th(): React.ReactNode { return makeTh("Type", "center"); },

  /** Render the type label. When `code` is given, dim the inner color on
   *  closed rows (the prior render(isPut) form ignored row state and the
   *  caller's `kindTone` wrap was dead because the inner color always won). */
  render(isPut: boolean, code?: string): React.ReactNode {
    const dim = code != null && kindDim(code);
    const cls = isPut
      ? (dim ? "text-green/70" : "text-green")
      : (dim ? "text-red/70" : "text-red");
    return <span className={cls}>{isPut ? "Put" : "Call"}</span>;
  },

  cell(code: string, isPut: boolean, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(isPut, code), "center", false, opts);
  },
};

// ---- Strike column (single-leg) -------------------------------------------

/**
 * Single-leg strike price (covered call short strike, etc.). Plain $n
 * formatted, white, dimmed by row code. For two-leg short/long pairs use
 * `strikesColumn` instead.
 */
export const strikeColumn = {
  key: "strike",
  header: "Strike",


  th(): React.ReactNode { return makeTh("Strike", "center"); },

  render(code: string, strike: number | null | undefined): React.ReactNode {
    if (strike == null) return <span className="text-foreground">—</span>;
    return <span className={toneClass("white", kindDim(code))}>{$n(strike)}</span>;
  },

  cell(code: string, strike: number | null | undefined, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(code, strike), "center", true, opts);
  },
};

// ---- MCap / ADV / OI columns (entry → current pair) -----------------------

/**
 * Market cap "entry / current" pair, formatted via `fmtMCap`. Neutral tone
 * (bright/dim per row code). Pass `null` for either side to render "—".
 */
export const mcapColumn = {
  key: "mcap",
  header: "MCap",


  th(): React.ReactNode { return makeTh("MCap", "center"); },

  render(code: string, entry: number | null | undefined, current: number | null | undefined): React.ReactNode {
    return <span className={kindDim(code) ? "text-muted" : "text-foreground"}>
      {fmtMCap(entry ?? null)} / {fmtMCap(current ?? null)}
    </span>;
  },

  cell(code: string, entry: number | null | undefined, current: number | null | undefined, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(code, entry, current), "center", true, opts);
  },
};

/**
 * Average daily volume "entry / current" pair, formatted via `fmtADV`.
 */
export const advColumn = {
  key: "adv",
  header: "ADV",


  th(): React.ReactNode { return makeTh("ADV", "center"); },

  render(code: string, entry: number | null | undefined, current: number | null | undefined): React.ReactNode {
    return <span className={kindDim(code) ? "text-muted" : "text-foreground"}>
      {fmtADV(entry ?? null)} / {fmtADV(current ?? null)}
    </span>;
  },

  cell(code: string, entry: number | null | undefined, current: number | null | undefined, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(code, entry, current), "center", true, opts);
  },
};

/**
 * Open interest "entry / current" pair, comma-formatted.
 */
export const oiColumn = {
  key: "oi",
  header: "OI",


  th(): React.ReactNode { return makeTh("OI", "center"); },

  render(code: string, entry: number | null | undefined, current: number | null | undefined): React.ReactNode {
    const fmt = (v: number | null | undefined) => v != null ? v.toLocaleString() : "—";
    return <span className={kindDim(code) ? "text-muted" : "text-foreground"}>
      {fmt(entry)} / {fmt(current)}
    </span>;
  },

  cell(code: string, entry: number | null | undefined, current: number | null | undefined, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(code, entry, current), "center", true, opts);
  },
};

// ---- Code column -----------------------------------------------------------

/**
 * The diagnostic row-code badge (e.g. "CCO", "SCO", "CPSO", "TOT"). Always
 * rendered bold cyan with a leading space — the standard "Code" cell across
 * every Ledger.
 *
 * **Admin-only (effective user):** ColumnTable drops any column with
 * `id === "code"` unless the *viewed* user is sudo — so a sudo admin viewing
 * as a normal user does not see Code either. Keep using `id: "code"`.
 */
export const codeColumn = {
  key: "code",
  header: "Code",


  th(): React.ReactNode { return makeTh("Code", "center"); },

  render(code: string): React.ReactNode {
    return <span className="font-bold text-cyan"> {code}</span>;
  },

  cell(code: string, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(code), "center", false, opts);
  },
};

// ---- Stop column -----------------------------------------------------------

/**
 * Stop / limit price for an open order. Always 100% white; no tone keying.
 * Caller passes the raw number (per-share); the column owns formatting.
 */
export const stopColumn = {
  key: "stop",
  header: "Stop",


  th(): React.ReactNode { return makeTh("Stop", "center"); },

  render(value: number | null | undefined): React.ReactNode {
    if (value == null) return <span className="text-foreground">—</span>;
    return <span className="text-foreground">{$n(value)}</span>;
  },

  cell(value: number | null | undefined, opts?: CellOpts): React.ReactNode {
    // Gate negative/zero broker prices to null at the column boundary so
    // callers don't repeat the same check.
    const v = value != null && value > 0 ? value : null;
    return makeTd(this.render(v), "center", true, opts);
  },
};

// ---- Status column ---------------------------------------------------------

/**
 * Broker-reported order status (e.g. "WORKING", "PENDING_ACTIVATION"). Always
 * amber regardless of row tone — the status itself is the signal, not the
 * row's open/closed state.
 */
export const statusColumn = {
  key: "status",
  header: "Status",


  th(): React.ReactNode { return makeTh("Status", "center"); },

  render(status: string): React.ReactNode {
    return <span className="text-amber">{status}</span>;
  },

  cell(status: string, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(status), "center", false, opts);
  },
};

// ---- Order-type column -----------------------------------------------------

/**
 * Human-readable order action for an Open Orders row, derived from the broker
 * `orderType`: Buy-Write, Buy to Close, Sell to Open, Buy/Sell Stock, Sell/Close
 * Spread. Buy-to-close → cyan, buy-write → green, everything else → amber.
 */
export const orderTypeColumn = {
  key: "ordertype",
  header: "Order",


  th(): React.ReactNode { return makeTh("Order", "center"); },

  cell(orderType: string, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(orderType), "center", false, opts);
  },

  render(orderType: string): React.ReactNode {
    const label =
      orderType === "BUY_WRITE" ? "Buy-Write"
        : orderType === "BUY_TO_OPEN" ? "Buy to Open"
        : orderType === "BUY_TO_CLOSE" ? "Buy to Close"
        : orderType === "SELL_TO_CLOSE" ? "Sell to Close"
        : orderType === "STOCK_BUY" ? "Buy Stock"
        : orderType === "STOCK_SELL" ? "Sell Stock"
        : orderType === "VERTICAL_SPREAD" ? "Sell Spread"
        : orderType === "VERTICAL_SPREAD_CLOSE" ? "Close Spread"
        : "Sell to Open";
    const color =
      orderType === "BUY_TO_CLOSE" ? "text-cyan"
        : orderType === "BUY_WRITE" ? "text-green"
        : "text-amber";
    return <span className={`font-medium ${color}`}>{label}</span>;
  },
};

// ---- Duration column -------------------------------------------------------

/**
 * Order Time-in-Force. Maps broker strings to short labels: GOOD_TILL_CANCEL
 * → GTC, DAY → DAY, anything else → passthrough. Always 100% white.
 */
export const durationColumn = {
  key: "duration",
  header: "Duration",


  th(): React.ReactNode { return makeTh("Duration", "center"); },

  render(duration: string): React.ReactNode {
    const text =
      duration === "GOOD_TILL_CANCEL" ? "GTC"
        : duration === "DAY" ? "DAY"
        : duration;
    return <span className="text-foreground">{text}</span>;
  },

  cell(duration: string, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(duration), "center", false, opts);
  },
};

// ---- Enter action column ---------------------------------------------------

/**
 * Single "Enter" button — the action on a scan/opportunity row that opens the
 * order ticket for that candidate. Bright green, white text. Caller wires the
 * click handler.
 */
export const enterActionColumn = {
  key: "action",
  header: "Action",


  th(): React.ReactNode { return makeTh("Action", "right"); },

  render(onEnter: () => void): React.ReactNode {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); onEnter(); }}
        className="text-[10px] px-2 py-0.5 rounded bg-green-hover text-white hover:bg-green transition-colors whitespace-nowrap"
      >
        Enter
      </button>
    );
  },

  cell(onEnter: () => void, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(onEnter), "right", false, { className: "pl-2", ...opts });
  },
};

// ---- Open-order actions column ---------------------------------------------

/**
 * The two buttons that live on every Open Orders row: Replace and Cancel.
 * Caller wires the click handlers and the per-row "cancelling" flag (so the
 * button can show "..." while the cancel request is in flight).
 */
export interface OpenOrderActionsProps {
  onReplace: () => void;
  onCancel: () => void;
  cancelling: boolean;
}

export const openOrderActionsColumn = {
  key: "actions",
  header: "Actions",


  th(): React.ReactNode { return makeTh("Actions", "center"); },

  cell(props: OpenOrderActionsProps, opts?: CellOpts): React.ReactNode {
    // Buttons own their own padding; cell uses py-1 with no pr.
    return (
      <td className={`py-1${opts?.className ? " " + opts.className : ""}`} title={opts?.title} colSpan={opts?.colSpan} onClick={opts?.onClick}>
        {this.render(props)}
      </td>
    );
  },

  render(props: {
    onReplace: () => void;
    onCancel: () => void;
    cancelling: boolean;
  }): React.ReactNode {
    return (
      <div className="flex gap-1 justify-end">
        <button
          onClick={props.onReplace}
          className="text-xs font-medium px-2 py-0.5 rounded bg-cyan/20 text-cyan hover:bg-cyan/30 transition-colors"
        >
          Replace
        </button>
        <button
          onClick={props.onCancel}
          disabled={props.cancelling}
          className="text-xs font-medium px-2 py-0.5 rounded bg-red/80 text-white hover:bg-red disabled:opacity-40 transition-colors"
        >
          {props.cancelling ? "..." : "Cancel"}
        </button>
      </div>
    );
  },
};

// ---- Description column ----------------------------------------------------

/**
 * Parse an OCC option symbol (e.g. `"RUT   240920P05000000"`). Pure, no page
 * state — a private copy so this module owns its symbol formatting. Returns
 * null for non-option symbols (equities / CUSIPs).
 */
function parseOCC(sym: string) {
  const m = sym.match(/^.+?\s+(\d{6})([CP])(\d{8})$/);
  if (!m) return null;
  const [, dateStr, pc, strikeStr] = m;
  const yy = parseInt(dateStr.slice(0, 2));
  const mm = parseInt(dateStr.slice(2, 4));
  const dd = parseInt(dateStr.slice(4, 6));
  return {
    exp: `${mm}/${dd}/${yy}`,
    expKey: dateStr,
    putCall: pc as "C" | "P",
    strike: parseInt(strikeStr) / 1000,
  };
}

/** Strike like `5000` or `4987.5` — integer when whole, else one decimal. */
function fmtStrike(k: number): string {
  return k % 1 === 0 ? k.toFixed(0) : k.toFixed(1);
}

/**
 * What a Description cell is — the row tells the column which one it is, and
 * the column owns how each looks. This is the single place that knows how to
 * describe every row kind across the Trades Summary and VS Trade History
 * tables.
 */
export type DescriptionInput =
  /** Plain text: an equity symbol, a group's underlying, "Stock", etc. */
  | { kind: "text"; text: string }
  /** A single option leg (child / standalone option rows). */
  | { kind: "option"; symbol: string }
  /** A main detail row holding an option position: underlying + faded leg. */
  | { kind: "position-option"; underlying?: string | null; symbol: string }
  /** A vertical spread (VS Trade History detail rows). */
  | {
      kind: "spread";
      spreadType: string;
      expirationDate: string;
      shortStrike: number;
      longStrike: number;
    };

export const descriptionColumn = {
  key: "description",
  header: "Description",
  // Trades Summary sorts the Description column by symbol.
  sortKey: "symbol",
  detail: false,



  /**
   * The one common function. Given what the row says it is, produce its
   * description. Both the Trades Summary table and the VS Trade History table
   * call this for every Description cell.
   */
  /** Canonical header — left-aligned, underlined. */
  th(): React.ReactNode {
    return makeTh(this.header, "left");
  },

  /** Canonical cell — left-aligned, code-driven dim. */
  cell(code: string, input: DescriptionInput, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(input, kindDim(code)), "left", false, opts);
  },

  render(input: DescriptionInput, dim?: boolean): React.ReactNode {
    const out = ((): React.ReactNode => {
      switch (input.kind) {
        case "text":
          return input.text;
        case "option": {
          const p = parseOCC(input.symbol);
          return p ? fmtOpt(p) : input.symbol;
        }
        case "position-option": {
          const p = parseOCC(input.symbol);
          return (
            <span>
              {input.underlying ?? ""}{" "}
              <span className="text-muted font-normal">
                {p ? fmtOpt(p) : input.symbol}
              </span>
            </span>
          );
        }
        case "spread": {
          const mm = parseInt(input.expirationDate.slice(5, 7));
          const dd = parseInt(input.expirationDate.slice(8, 10));
          const yy = input.expirationDate.slice(2, 4);
          const pc = input.spreadType === "bull_put" ? "Put" : "Call";
          return `${mm}/${dd}/${yy} ${fmtStrike(input.shortStrike)}/${fmtStrike(input.longStrike)} ${pc}`;
        }
      }
    })();
    return dimWrap(out, dim);
  },
};

const DAY_MS = 86400000;

// ---- Days column -----------------------------------------------------------

/**
 * What a Days cell measures — the row says which, the column owns the math.
 * Every formula here is byte-for-byte the one it replaced.
 */
export type DaysInput =
  /** Days until an OCC option's expiry (Trades Summary option legs/rows). */
  | { kind: "dte-occ"; symbol: string }
  /** Days an equity position has been held (open since a date). */
  | { kind: "held-since"; since: string | null | undefined }
  /** Days a leg was held — open..close (or open..today when still open). */
  | { kind: "held-range"; start: string; end: string | null | undefined }
  /** Days until expiry from a YYYY-MM-DD date, ET-normalized (VS open). */
  | { kind: "dte-date"; expirationDate: string };

export const daysColumn = {
  key: "days",
  header: "Days",
  sortKey: "days",
  detail: true,

  /** Sortable header (Trades Summary). Caller composes the full class
   *  (it includes the collapse + right-align classes). */


  /** Canonical header — right-aligned, underlined. */
  th(): React.ReactNode { return makeTh("Days", "right"); },

  /** Canonical cell — right-aligned, code-driven byKind tone. */
  cell(code: string, input: DaysInput, opts?: CellOpts): React.ReactNode {
    return makeTd(this.byKind(code, input), "right", false, opts);
  },

  /** Column-owned coloration keyed by the row's diagnostic code:
   *  open option → amber (DTE); LEO/M → bright white; LEC/closed option
   *  → dim white; group/total → blank. Caller passes only code + input. */
  byKind(code: string, input: DaysInput): React.ReactNode {
    const k = semKind(code);
    if (k === "MGO" || k === "TOT" || k === "CASH" || k === "OGO" || k === "OGC" || k === "MGC") return "";
    if (k === "OPTO" || k === "CSPO") return this.render(input, false, "amber");
    if (k === "LEC" || k === "OPTC" || k === "CSPC") return this.render(input, true);
    return this.render(input, false); // LEO / M → bright white
  },

  render(
    input: DaysInput,
    dim?: boolean,
    color?: "amber",
  ): React.ReactNode {
    const out = ((): number | string => {
      switch (input.kind) {
        case "dte-occ": {
          const p = parseOCC(input.symbol);
          if (!p) return "";
          const [mm, dd, yy] = p.exp.split("/").map(Number);
          const exp = new Date(2000 + yy, mm - 1, dd);
          return Math.max(0, Math.ceil((exp.getTime() - Date.now()) / DAY_MS));
        }
        case "held-since": {
          if (!input.since) return "";
          return (
            Math.floor((Date.now() - new Date(input.since).getTime()) / DAY_MS) + 1
          );
        }
        case "held-range": {
          const end = input.end ? new Date(input.end).getTime() : Date.now();
          const start = new Date(input.start).getTime();
          return Math.max(0, Math.floor((end - start) / DAY_MS)) + 1;
        }
        case "dte-date": {
          const nowET = new Date(
            new Date().toLocaleString("en-US", { timeZone: "America/New_York" }),
          );
          const todayET = new Date(
            nowET.getFullYear(),
            nowET.getMonth(),
            nowET.getDate(),
          );
          const expDate = new Date(input.expirationDate + "T00:00:00");
          return Math.max(
            0,
            Math.round((expDate.getTime() - todayET.getTime()) / DAY_MS),
          );
        }
      }
      return "";
    })();
    if (color === "amber")
      return (
        <span className={dim ? "text-amber/70" : "text-amber"}>{out}</span>
      );
    return dimWrap(out, dim);
  },
};

// ---- Qty column ------------------------------------------------------------

/** What a Qty cell shows — the row says which form. */
export type QtyInput =
  /** A net position quantity; whole when fractional shows the integer part. */
  | { kind: "net"; qty: number; fractional: boolean }
  /** A signed contract count (short renders negative). */
  | { kind: "signed"; count: number; short: boolean }
  /** A plain count (VS contracts). */
  | { kind: "plain"; count: number };

export const qtyColumn = {
  key: "qty",
  header: "Qty",
  sortKey: "qty",
  detail: true,



  /** Canonical header — center-aligned, underlined. */
  th(): React.ReactNode {
    return makeTh(this.header, "center");
  },

  /** Canonical cell — center-aligned, font-mono, code-driven tone. */
  cell(code: string, input: QtyInput, opts?: CellOpts): React.ReactNode {
    return makeTd(kindTone(code, this.render(input)), "center", true, opts);
  },

  render(input: QtyInput, dim?: boolean): React.ReactNode {
    const out = ((): number | string => {
      switch (input.kind) {
        case "net":
          return input.fractional ? input.qty.toFixed(0) : input.qty;
        case "signed":
          return input.short ? -input.count : input.count;
        case "plain":
          return input.count;
      }
      return "";
    })();
    return dimWrap(out, dim);
  },
};

// ---- Price / Last / Entry columns ------------------------------------------
//
// These differ from the columns above: the *number* is derived from page
// state (cost basis, live prices, option premium, fees) with a different
// formula per row kind, and that state lives on each page. So the page still
// computes the raw number; the column owns the *presentation* — the dollar /
// per-share formatting, the amber "---" missing state, and the blank state.
// Cell color stays page-owned (long/short, open/closed differ per table).

export const amberDash = <span className="text-amber">---</span>;

/**
 * What the cell should show, given a number the page already derived:
 *  - `fixed2`  : 2-decimal price, or amber "---" when null  (per-share entry/last)
 *  - `d-dash`  : dollars, or amber "---" when null           (total entry/basis)
 *  - `n`       : per-share number ($n)
 *  - `n-blank` : per-share number, or "" when null
 *  - `d`       : dollars ($d)
 */
export type MoneyInput =
  | { kind: "fixed2"; value: number | null }
  | { kind: "d-dash"; value: number | null }
  | { kind: "n"; value: number }
  | { kind: "n-blank"; value: number | null }
  | { kind: "d"; value: number }
  | { kind: "d-blank"; value: number | null }
  // MktVal everywhere: ONE rule. Pass the true signed value. A negative
  // renders red with a leading minus; non-negative uses plain $d and the
  // page's own positive coloring. `empty: "dash"` shows amber "---" for a
  // null value (rows with no market value); otherwise null is blank.
  | { kind: "mktval"; value: number | null; empty?: "dash" }
  // Income/Growth/Profit: leading minus on negatives only (expected to be
  // positive; no "+" on positives, color conveys direction).
  // `d-zeroblank`: blank when zero or missing. `g`/`g-blank`: realized
  // amount, `g-blank` blank when null.
  | { kind: "d-zeroblank"; value: number | null }
  | { kind: "g"; value: number }
  | { kind: "g-blank"; value: number | null }
  // Like `g`, but a null value renders the amber "---" placeholder
  // (e.g. a TOTAL growth with no cost basis to compare against).
  | { kind: "g-dash"; value: number | null };

// MktVal / Income / Growth / Profit are expected to be positive, so a
// negative is worth flagging: show a leading minus (negatives only — no
// "+" on positives; the green/red color still conveys direction).
function signedDollars(value: number): string {
  return value < 0 ? `-${$d(Math.abs(value))}` : $d(value);
}

function moneyContent(input: MoneyInput): React.ReactNode {
  switch (input.kind) {
    case "fixed2":
      return input.value == null ? amberDash : input.value.toFixed(2);
    case "d-dash":
      return input.value == null ? amberDash : $d(input.value);
    case "n":
      return $n(input.value);
    case "n-blank":
      return input.value == null ? "" : $n(input.value);
    case "d":
      return $d(input.value);
    case "d-blank":
      return input.value == null ? "" : $d(input.value);
    case "mktval":
      if (input.value == null) return input.empty === "dash" ? amberDash : "";
      if (input.value < 0)
        return (
          <span className="text-red">{signedDollars(input.value)}</span>
        );
      return $d(input.value);
    case "d-zeroblank":
      return !input.value ? "" : signedDollars(input.value);
    case "g":
      return signedDollars(input.value);
    case "g-blank":
      return input.value == null ? "" : signedDollars(input.value);
    case "g-dash":
      return input.value == null ? amberDash : signedDollars(input.value);
  }
}

// ---- Row-kind-driven coloration (column-owned; caller passes only the
// row's diagnostic code + the raw value). One matrix lives here so every
// table colors identically. ----------------------------------------------
export type SemKind = "MGO" | "LEO" | "LEC" | "OPTO" | "OPTC" | "TOT" | "CASH" | "OGO" | "OGC" | "MGC" | "CSPO" | "CSPC" | "CCOP" | "CCCP";

// Credit-spread row codes: Credit Call/Put Spread, suffixed by state. Call vs
// put doesn't affect coloration — only open/closed state does (all opens color
// like a generic credit-spread-open, all closes like closed):
//   O = held-open position, C = closed position,
//   N = pending eNtry order (sell-to-open), X = pending eXit order
//   (buy-to-close), P = scan oPportunity (a candidate, not yet ordered).
//   A pending order or opportunity colors/formats like an open.
const CREDIT_OPEN = new Set(["CCSO", "CPSO", "CPSN", "CPSX", "CCSN", "CCSX", "CPSP", "CCSP"]);
const CREDIT_CLOSED = new Set(["CCSC", "CPSC"]);

// 0DTE vertical-spread row codes mirror the credit-spread family (Z prefix =
// zero-DTE). Same coloration semantics: open/pending bright, closed dim.
const CREDIT_0DTE_OPEN = new Set(["ZCSO", "ZPSO", "ZPSN", "ZPSX", "ZCSN", "ZCSX", "ZPSP", "ZCSP"]);
const CREDIT_0DTE_CLOSED = new Set(["ZCSC", "ZPSC"]);

// Covered-call-realm pending working orders (not yet filled). Suffix N = eNtry,
// X = eXit: CCN = buy-write (enter a covered call), SCN = sell-to-open a
// standalone call, SCX = buy-to-close a call, LEN = buy stock, LEX = sell
// stock. All render bright/open (neutral tone) — the Code column carries the
// action, the Type column the readable label.
const ORDER_OPEN = new Set(["CCN", "SCN", "SCX", "LEN", "LEX"]);

export function semKind(code: string): SemKind {
  const c = code.trim();
  if (c === "OGO") return "OGO";
  if (c === "OGC") return "OGC";
  if (c === "MGC") return "MGC";
  if (c === "G" || c === "MGO") return "MGO";
  if (c === "$$$") return "CASH";
  if (c === "TOT") return "TOT";
  if (c === "LEO" || c === "SEO") return "LEO";
  if (c === "LEC" || c === "SEC") return "LEC";
  if (CREDIT_OPEN.has(c)) return "CSPO"; // credit spread open / pending order
  if (CREDIT_CLOSED.has(c)) return "CSPC"; // credit spread closed
  if (CREDIT_0DTE_OPEN.has(c)) return "CSPO"; // 0DTE credit spread open / pending order
  if (CREDIT_0DTE_CLOSED.has(c)) return "CSPC"; // 0DTE credit spread closed
  if (ORDER_OPEN.has(c)) return "OPTO"; // CC-realm pending working order → bright
  if (c === "CCO") return "CCOP"; // covered call open (2-leg position: long stock + short call)
  if (c === "CCC") return "CCCP"; // covered call closed
  // option 3-letter codes (<side><P|C><O|C>) and pending-order codes.
  // Only a CLOSED position (suffix C) dims; everything else — open (O),
  // pending entry (N), pending exit (X), opportunity (P) — is full-intensity.
  return c[c.length - 1] === "C" ? "OPTC" : "OPTO";
}

// Neutral columns (Date, Description, Qty, Price, the VS metrics):
// bright white for G / M / open option / LEO / TOTAL; dim white for LEC and
// closed options. Returns the `dim` flag the shared renders already accept.
export function kindDim(code: string): boolean {
  const k = semKind(code);
  return k === "LEC" || k === "OPTC" || k === "OGC" || k === "MGC" || k === "CSPC" || k === "CCCP";
}

// Wrap an already-built node (e.g. a VS metric pair) in the neutral tone.
export function kindTone(code: string, node: React.ReactNode): React.ReactNode {
  return (
    <span className={kindDim(code) ? "text-muted" : "text-foreground"}>
      {node}
    </span>
  );
}

type MoneyRole = "mktval" | "income" | "growth" | "profit" | "entry" | "exit" | "last";

function toneClass(tone: "green" | "red" | "white", dim: boolean): string {
  return tone === "green"
    ? dim ? "text-green/70" : "text-green"
    : tone === "red"
      ? dim ? "text-red/70" : "text-red"
      : dim ? "text-muted" : "text-foreground";
}

// Entry: LEO → bright red; LEC → dim red; credit/CC short premium → green
// (dim when closed); option → short green / long red. Group (OGO/OGC) and
// TOTAL: rolled-up open credit (VS Trade History). Always magnitude (no
// sign). Identity on open credit spreads: Entry + MktVal = unrealized
// (= Open Spreads Income). Caller passes code + raw value.
function entryByKind(code: string, value: number | null | undefined): React.ReactNode {
  const c = code.trim();
  if (c === "$$$" || c === "MGO" || c === "MGC") return "";
  if (value == null) return "";
  let tone: "green" | "red" | "white";
  let dim: boolean;
  if (c === "TOT" || c === "OGO" || c === "OGC") {
    // Open-book collected premium rollup (VS Trade History).
    return <span className={toneClass("green", c === "OGC")}>{$d(Math.abs(value))}</span>;
  }
  if (c === "LEO") { tone = "red"; dim = false; }
  else if (c === "LEC") { tone = "red"; dim = true; }
  else if (CREDIT_OPEN.has(c) || CREDIT_CLOSED.has(c)
    || CREDIT_0DTE_OPEN.has(c) || CREDIT_0DTE_CLOSED.has(c)) {
    tone = "green";
    dim = CREDIT_CLOSED.has(c) || CREDIT_0DTE_CLOSED.has(c);
  }
  else if (c === "CCO" || c === "CCC") { tone = "green"; dim = c === "CCC"; } // covered call = short premium received
  else { tone = c[0] === "S" ? "green" : "red"; dim = c[c.length - 1] === "C"; }
  return <span className={toneClass(tone, dim)}>{$d(Math.abs(value))}</span>;
}

// Exit: M/G/total/LEO → blank; LEC → dim green; closed option → short red /
// long green, dim; open option → blank. Always magnitude (no sign).
function exitByKind(code: string, value: number | null | undefined): React.ReactNode {
  const c = code.trim();
  if (c === "TOT" || c === "LEO" || c === "$$$" || c === "OGO" || c === "OGC" || c === "MGO" || c === "MGC") return "";
  if (value == null) return "";
  let tone: "green" | "red";
  if (c === "LEC") tone = "green";
  else if (CREDIT_OPEN.has(c)) return ""; // credit spread still open → no exit
  else if (CREDIT_CLOSED.has(c)) tone = "red"; // credit spread closed (short) → red
  else if (c === "CCO") return ""; // covered call still open → no exit
  else if (c === "CCC") tone = "red"; // covered call closed (short) → red
  else {
    if (c[c.length - 1] !== "C") return ""; // open option has no exit
    tone = c[0] === "S" ? "red" : "green";
  }
  return <span className={toneClass(tone, true)}>{$d(Math.abs(value))}</span>;
}

function moneyByKind(
  role: MoneyRole,
  code: string,
  value: number | null | undefined,
): React.ReactNode {
  const k = semKind(code);
  // Cash row: only MktVal shows (100% white, as-is); everything else blank.
  if (k === "CASH") {
    if (role !== "mktval" || value == null) return "";
    return <span className="text-foreground">{signedDollars(value)}</span>;
  }
  // Credit spread. Open: MktVal = close-cost liability (red, signed). Income is
  // realized only → blank while open. Closed: MktVal blank; Income = realized
  // (dim green/red/white). Open unrealized = Entry (premium) + MktVal, which
  // matches Open Spreads Income (page-owned Entry dollars).
  if (k === "CSPO" || k === "CSPC") {
    const d = k === "CSPC";
    if (role === "mktval") {
      if (d || value == null) return "";
      return <span className="text-red">{signedDollars(value)}</span>;
    }
    if (role === "income") {
      if (!d || value == null) return "";
      const t = value > 0 ? "green" : value < 0 ? "red" : "white";
      return <span className={toneClass(t, true)}>{signedDollars(value)}</span>;
    }
    return ""; // growth / profit not used for spreads
  }
  // Covered call position (long stock + short call). All three money columns
  // are live and meaningful: Income = premium received (always positive
  // → green), Growth = stock-side unrealized, Profit = Income + Growth.
  // MktVal is intentionally blank (the two legs net out across columns).
  if (k === "CCOP" || k === "CCCP") {
    const d = k === "CCCP";
    if (role === "mktval") return "";
    if (value == null) return "";
    const t = value > 0 ? "green" : value < 0 ? "red" : "white";
    const text = value < 0 ? `-${$d(Math.abs(value))}` : $d(value);
    return <span className={toneClass(t, d)}>{text}</span>;
  }
  const g = k === "MGO" || k === "TOT" || k === "MGC";
  // spec: [empty, intensity, scheme, signNeg]
  let empty = false;
  let dim = false;
  let scheme: "wn" | "grw" | "white" = "grw";
  let signNeg = true;
  if (k === "OGO" || k === "OGC") {
    // Option-only group: Income (and Profit, which mirrors Income) shown
    // green/red/white; MktVal only on open (white/red/white); everything
    // else blank. OGC dim, OGO bright.
    if (role === "income" || role === "profit") { scheme = "grw"; dim = k === "OGC"; }
    else if (role === "mktval" && k === "OGO") { scheme = "wn"; }
    else empty = true;
  } else if (role === "mktval") {
    if (k === "OPTC" || k === "LEC" || k === "MGC") empty = true;
    else { scheme = "wn"; } // G/OPTO/LEO/TOT
  } else if (role === "income") {
    if (g) { scheme = "grw"; }
    else if (k === "OPTC") { scheme = "grw"; dim = true; signNeg = false; }
    else empty = true; // LEO/LEC/OPTO/M
  } else if (role === "growth") {
    if (g || k === "LEO") { scheme = "grw"; }
    else if (k === "LEC") { scheme = "grw"; dim = true; }
    else empty = true; // OPTO/OPTC
  } else {
    // profit
    if (g) { scheme = "grw"; }
    else empty = true;
  }
  if (k === "MGC") dim = true; // closed mixed group: all dim
  if (empty) return "";
  if (value == null) return "";
  if (k === "TOT" && value === 0) return ""; // TOTAL rows: blank at zero
  const v = value;
  const text =
    v < 0 ? (signNeg ? `-${$d(Math.abs(v))}` : $d(Math.abs(v))) : $d(v);
  let tone: "green" | "red" | "white";
  if (scheme === "wn") tone = v < 0 ? "red" : "white";
  else tone = v > 0 ? "green" : v < 0 ? "red" : "white";
  const cls =
    tone === "green"
      ? dim
        ? "text-green/70"
        : "text-green"
      : tone === "red"
        ? dim
          ? "text-red/70"
          : "text-red"
        : dim
          ? "text-muted"
          : "text-foreground";
  return <span className={cls}>{text}</span>;
}

function makeMoneyColumn(header: string, sortKey: string, role?: MoneyRole) {
  return {
    key: sortKey,
    header,
    sortKey,
    detail: true,
    role,
    /** Canonical header — right-aligned, underlined. */
    th(): React.ReactNode {
      return makeTh(header, "right");
    },
    /** Canonical cell — right-aligned, font-mono, byKind coloration when the
     *  column has a role (entry/exit/mktval/income/growth/profit); otherwise
     *  caller-supplied MoneyInput rendered with kindDim. */
    cell(code: string, valueOrInput: number | null | undefined | MoneyInput, opts?: CellOpts): React.ReactNode {
      const content =
        typeof valueOrInput === "object" && valueOrInput !== null && "kind" in valueOrInput
          ? this.render(valueOrInput, kindDim(code))
          : this.byKind(code, valueOrInput as number | null | undefined);
      return makeTd(content, "right", true, opts);
    },
    /** Canonical Last cell — right-aligned, font-mono, comparison-vs-ref tone. */
    cellLast(code: string, input: MoneyInput, ref: number | null | undefined, opts?: CellOpts): React.ReactNode {
      return makeTd(this.byKindLast(code, input, ref), "right", true, opts);
    },
    /** Canonical colored cell — right-aligned, font-mono, green/red/white regardless of kind. */
    cellColored(code: string, value: number | null | undefined, opts?: CellOpts): React.ReactNode {
      return makeTd(this.colored(code, value), "right", true, opts);
    },
    /** Column-owned coloration keyed by the row's diagnostic code. The
     *  caller passes only the code and the raw signed value. */
    byKind(code: string, value: number | null | undefined): React.ReactNode {
      if (role === "entry") return entryByKind(code, value);
      if (role === "exit") return exitByKind(code, value);
      return moneyByKind(role ?? "mktval", code, value);
    },
    /** Unconditional green/red/white money (positive green, negative red,
     *  zero white), magnitude with a leading minus on negatives. Intensity
     *  follows the row code (open → 100%, closed → dim). Unlike `byKind`,
     *  this does not blank out by row kind — used where a column must always
     *  show its value (e.g. the Open Vertical Spreads ledger Income). */
    colored(code: string, value: number | null | undefined): React.ReactNode {
      if (value == null) return "";
      const tone: "green" | "red" | "white" =
        value > 0 ? "green" : value < 0 ? "red" : "white";
      const text = value < 0 ? `-${$d(Math.abs(value))}` : $d(value);
      return (
        <span className={toneClass(tone, kindDim(code))}>{text}</span>
      );
    },
    /** Last: column owns the Last-vs-Price comparison + intensity, keyed by
     *  the row code. G/total → blank. Short legs favor below; long/equity
     *  favor above. Bright for M / LEO / open option; dim otherwise. */
    byKindLast(
      code: string,
      input: MoneyInput,
      ref: number | null | undefined,
    ): React.ReactNode {
      const c = code.trim();
      if (c === "TOT" || c === "$$$" || c === "OGO" || c === "OGC" || c === "MGO" || c === "MGC") return "";
      let dim: boolean;
      let favorable: "below" | "above";
      if (c === "LEO") { dim = false; favorable = "above"; }
      else if (c === "LEC") { dim = true; favorable = "above"; }
      else if (CREDIT_OPEN.has(c) || CREDIT_CLOSED.has(c)) { favorable = "below"; dim = CREDIT_CLOSED.has(c); } // credit spread = short
      else if (c === "CCO" || c === "CCC") { favorable = "below"; dim = c === "CCC"; } // covered call = short
      else {
        favorable = c[0] === "S" ? "below" : "above";
        dim = c[c.length - 1] === "C";
      }
      return this.render(input, dim, undefined, { ref, favorable });
    },
    render(
      input: MoneyInput,
      dim?: boolean,
      color?: "green" | "red" | "sign" | "foreground",
      cmp?: { ref: number | null | undefined; favorable: "below" | "above" },
    ): React.ReactNode {
      const node = moneyContent(input);
      const raw = (input as { value?: number | null }).value;
      const v = raw ?? 0;
      let base: "green" | "red" | "foreground" | null = null;
      if (cmp && cmp.ref != null && raw != null) {
        // Compare value to a reference (e.g. Last vs Price). `favorable`
        // is the direction that's good (green): short legs → below,
        // long → above.
        base =
          v < cmp.ref
            ? cmp.favorable === "below"
              ? "green"
              : "red"
            : v > cmp.ref
              ? cmp.favorable === "below"
                ? "red"
                : "green"
              : "foreground";
      } else if (color === "sign") {
        base = v > 0 ? "green" : v < 0 ? "red" : "foreground";
      } else if (
        color === "green" ||
        color === "red" ||
        color === "foreground"
      ) {
        base = color;
      }
      if (base === null) return dimWrap(node, dim);
      // Explicit literals so Tailwind keeps the classes.
      const cls =
        base === "green"
          ? dim
            ? "text-green/70"
            : "text-green"
          : base === "red"
            ? dim
              ? "text-red/70"
              : "text-red"
            : dim
              ? "text-muted"
              : "text-foreground";
      return <span className={cls}>{node}</span>;
    },
  };
}

// Trades Summary sorts Entry by the "basis" key; Price/Last by their own.
export const priceColumn = makeMoneyColumn("Price", "price");
// Open Orders: the working order's limit, same formatting as Price.
export const limitColumn = makeMoneyColumn("Limit", "limit");

/**
 * Absolute return for a credit spread: limit / max loss × 100,
 * where max loss = width − limit (per share). Null when either
 * side is missing or non-positive.
 */
export function absReturnFromLimit(limit: number, width: number): number | null {
  if (!(limit > 0) || !(width > 0)) return null;
  const maxLoss = width - limit;
  if (!(maxLoss > 0)) return null;
  return (limit / maxLoss) * 100;
}

/**
 * Open Orders Abs Return — integer percent, same formula as the scan
 * Abs Return column but using the working limit (not a fee-netted fill).
 */
export const absReturnColumn = {
  key: "absret",
  header: "Abs Return",

  th(): React.ReactNode {
    return makeTh(<>Abs<br />Return</>, "center");
  },

  cell(code: string, value: number | null | undefined, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(code, value), "center", true, {
      title: "Absolute return: working limit / max loss (width − limit)",
      ...opts,
    });
  },

  render(code: string, value: number | null | undefined): React.ReactNode {
    if (value == null) return kindTone(code, "—");
    return kindTone(code, `${value.toFixed(0)}%`);
  },
};

export const lastColumn = makeMoneyColumn("Last", "last", "last");
export const entryColumn = makeMoneyColumn("Entry", "basis", "entry");
// Exit is never sortable (the caller's TableColumn descriptor omits sortKey).
export const exitColumn = makeMoneyColumn("Exit", "exit", "exit");
// Trades Summary sorts MktVal by "mktval", Income by the "dist" key.
export const mktvalColumn = makeMoneyColumn("MktVal", "mktval", "mktval");
export const incomeColumn = makeMoneyColumn("Income", "dist", "income");

/**
 * Inc/Prem: "$income / $premium" in one cell — income colored green/red/white
 * like `incomeColumn.colored`, premium green like `premiumColumn`.
 */
export const incPremColumn = {
  key: "incprem",
  header: "Inc/Prem",

  th(): React.ReactNode { return makeTh("Inc/Prem", "center"); },

  cell(code: string, income: number | null | undefined, premium: number | null | undefined, opts?: CellOpts): React.ReactNode {
    return makeTd(this.render(code, income, premium), "center", true, opts);
  },

  render(code: string, income: number | null | undefined, premium: number | null | undefined): React.ReactNode {
    if (income == null && premium == null) return "";
    return (
      // Equal-width halves around the "/" so it sits at the column's center
      // on every row, with income right-aligned and premium left-aligned.
      <span className="grid w-full grid-cols-[1fr_auto_1fr]">
        <span className="text-right">{income != null ? incomeColumn.colored(code, income) : "—"}</span>
        <span className="text-foreground/50 px-1">/</span>
        <span className="text-left">{premium != null ? premiumColumn.render(code, premium) : "—"}</span>
      </span>
    );
  },
};
// Growth sorts by "gain"; Profit is not sortable (caller omits sortKey).
export const growthColumn = makeMoneyColumn("Growth", "gain", "growth");
export const profitColumn = makeMoneyColumn("Profit", "profit", "profit");

// ---- Entry / second metric columns (VS Trade History) ----------------------
//
// P(ITM), eP(ITM), HV%, IV/HV, Prem% all show an "entry / second" pair where
// "second" is the exit value for a closed trade or the live/current value for
// an open one. A trade that expired worthless shows the amber "X" instead of
// the second value. The page derives the numbers (they're page-state bound);
// these own the header + the shared pair formatting.

/** Shown in place of the second value when the option expired worthless. */
export const metricExpSpan = <span className="text-amber">X</span>;

/**
 * Percent pair (P(ITM) / eP(ITM) / Prem%): the "%" goes ONLY after the second
 * value — unless expired, where the "%" goes after the first value and the
 * second is the "X" badge. Missing side renders "—".
 */
export function renderPctPair(
  entry: number | null | undefined,
  second: number | null | undefined,
  expired: boolean,
  fmt: (n: number) => string,
): React.ReactNode {
  const e = entry != null ? fmt(entry) : null;
  if (expired) return <>{e != null ? `${e}%` : "—"} / {metricExpSpan}</>;
  const s = second != null ? fmt(second) : null;
  return <>{e ?? "—"} / {s != null ? `${s}%` : "—"}</>;
}

/**
 * Colored percent pair (P(ITM) / eP(ITM)): first value plain white; second
 * value compared to the first — green when it moved below the entry, red
 * above, white if equal. Expired → second is the amber "X". `dim` mutes
 * everything one notch (closed credit spread).
 */
export function renderPctPairColored(
  entry: number | null | undefined,
  second: number | null | undefined,
  expired: boolean,
  fmt: (n: number) => string,
  dim: boolean,
  expiredZeroGreen = false,
  secondOnly = false,
): React.ReactNode {
  const white = dim ? "text-muted" : "text-foreground";
  const e = entry != null ? fmt(entry) : null;
  const first = <span className={white}>{e ?? "—"}</span>;
  const lead = secondOnly ? null : <>{first} / </>;
  if (expired) {
    const expNode =
      expiredZeroGreen && dim ? (
        <span className={toneClass("green", true)}>0%</span>
      ) : (
        metricExpSpan
      );
    return secondOnly ? expNode : <>{first} /{" "}{expNode}</>;
  }
  if (second == null || entry == null) {
    return <>{lead}<span className={white}>{second != null ? `${fmt(second)}%` : "—"}</span></>;
  }
  const tone = second < entry ? "green" : second > entry ? "red" : "white";
  return <>{lead}<span className={toneClass(tone, dim)}>{`${fmt(second)}%`}</span></>;
}

/**
 * Plain pair (HV% / IV/HV): "entry / second", no "%", "X" when expired.
 * Callers pass already-resolved nodes (a number, a preformatted string, or
 * "—").
 */
export function renderPlainPair(
  entry: React.ReactNode,
  second: React.ReactNode,
  expired: boolean,
): React.ReactNode {
  return <>{entry ?? "—"} / {expired ? metricExpSpan : second ?? "—"}</>;
}

type MetricKind = "pct-colored" | "pct" | "plain";

/** A metric column owns its header, its canonical center-alignment, and its
 *  pair-or-single render. Callers pass the row code + an entry value + an
 *  optional second value (current/exit). `pct-colored` = P(ITM)/eP(ITM) style
 *  (second compared vs entry, green if moved favorably for a short, red if
 *  not); `pct` = same shape but no comparison color; `plain` = HV% / IV/HV
 *  pair where caller supplies pre-formatted nodes. */
function makeMetricColumn(header: string, kind: MetricKind, fmt: (n: number) => string) {
  return {
    header,
    kind,
    /** Canonical header — center-aligned, underlined. */
    th(): React.ReactNode {
      return makeTh(header, "center");
    },
    /** Render a single value (entry-only). Em-dash when null. Dimmed by code. */
    renderOne(code: string, entry: number | null | undefined): React.ReactNode {
      const dim = kindDim(code);
      const txt = entry == null ? "—" : `${fmt(entry)}%`;
      return <span className={dim ? "text-muted" : "text-foreground"}>{txt}</span>;
    },
    /** Render an entry / second pair using the column's kind. `expired` shows
     *  the amber X in the second slot. */
    renderPair(
      code: string,
      entry: number | null | undefined,
      second: number | null | undefined,
      expired = false,
    ): React.ReactNode {
      const dim = kindDim(code);
      if (kind === "pct-colored") {
        return renderPctPairColored(entry, second, expired, fmt, dim);
      }
      if (kind === "pct") {
        return renderPctPair(entry, second, expired, fmt);
      }
      // plain: caller may pass numbers; format as fixed strings.
      const e = entry != null ? fmt(entry) : "—";
      const s = second != null ? fmt(second) : "—";
      return renderPlainPair(e, s, expired);
    },
    /** Canonical cell — center-aligned, font-mono. Single-value form. */
    cellOne(code: string, entry: number | null | undefined, opts?: CellOpts): React.ReactNode {
      return makeTd(this.renderOne(code, entry), "center", true, opts);
    },
    /** Canonical cell — center-aligned, font-mono. Entry/second pair form. */
    cellPair(
      code: string,
      entry: number | null | undefined,
      second: number | null | undefined,
      expired = false,
      opts?: CellOpts,
    ): React.ReactNode {
      return makeTd(this.renderPair(code, entry, second, expired), "center", true, opts);
    },
  };
}

const fmt1 = (n: number) => n.toFixed(1);
const fmt0 = (n: number) => n.toFixed(0);

export const probItmColumn = makeMetricColumn("P(ITM)", "pct-colored", fmt1);
export const eProbItmColumn = makeMetricColumn("eP(ITM)", "pct-colored", fmt1);
export const eProbTouchColumn = makeMetricColumn("eP(Touch)", "pct-colored", fmt1);
// Pattern P(ITM) — read-only from the per-day pattern cache (warmed via the
// Patterns page). Same colored-percent rendering as eP(ITM); dash when uncached.
export const pProbItmColumn = makeMetricColumn("pP(ITM)", "pct-colored", fmt0);
export const hvPctColumn = makeMetricColumn("HV%", "plain", fmt1);
export const ivHvColumn = makeMetricColumn("IV/HV", "plain", fmt1);

// Combined ITM column — Black-Scholes, Empirical, and Pattern P(ITM) collapsed
// into one cell as percents joined by " / ", under a two-line
// "P(ITM)" / "B/S/Emp/Pat" header (the legend names the three values in order).
// Each value is already a percent (0–100); a null slot shows an em-dash.
// Closed rows are dimmed.
export const itmColumn = {
  /** Two-line header: "P(ITM)" over the B/S/Emp/Pat legend. `pad` overrides the
   *  canonical header padding (the scan tables use "py-2 pr-[15px]"). */
  th(pad: string = CELL_BASE): React.ReactNode {
    return (
      <th className={`${pad} underline text-center align-bottom`}>
        P(ITM)<br /><span className="text-[10px] opacity-70" style={{ fontWeight: 700 }}>B/S/Emp/Pat</span>
      </th>
    );
  },
  /** Cell: the three percents (Black-Scholes, Empirical, Pattern) joined by
   *  " / ", in legend order. B/S is neutral; Emp and Pat are red when they
   *  exceed B/S and green otherwise — compared at full precision even though
   *  the display is rounded to whole percents (so 10.0 / 10.1 / 10.1 shows
   *  10 / 10(red) / 10(red)). A value with no B/S to compare against, or a
   *  null value, stays neutral. */
  cell(
    code: string,
    bs: number | null | undefined,
    emp: number | null | undefined,
    pat: number | null | undefined,
    opts?: CellOpts,
  ): React.ReactNode {
    const dim = kindDim(code);
    const neutral = dim ? "text-muted" : "text-foreground";
    const fmt = (n: number) => `${n.toFixed(0)}%`;
    const cmp = (v: number | null | undefined): React.ReactNode => {
      if (v == null) return <span className={neutral}>—</span>;
      if (bs == null) return <span className={neutral}>{fmt(v)}</span>;
      const tone = v > bs
        ? (dim ? "text-red/70" : "text-red")
        : (dim ? "text-green/70" : "text-green");
      return <span className={tone}>{fmt(v)}</span>;
    };
    return makeTd(
      <span className={neutral}>{bs == null ? "—" : fmt(bs)} / {cmp(emp)} / {cmp(pat)}</span>,
      "center", true, opts);
  },
};

// ---- Generic column-driven table renderer ---------------------------------
//
// A table is an ordered list of TableColumn descriptors plus a list of
// normalized rows. ONE list drives the header AND every row, so adding /
// removing a column or toggling its collapse visibility is a one-line edit
// to the column array — never per-row-variant hand edits.
//
// Each column owns its full <th> and <td> (so it controls className /
// colSpan). `cell` may return null to emit nothing for that column on a
// given row (used for colSpan spanning). The page wraps each row's cells in
// its own <tr> via `renderRow` (so it keeps key/className/onClick).

export interface TableColumn<R> {
  id: string;
  /** Hidden when the table is collapsed (header AND body together). */
  expandedOnly?: boolean;
  /** Full <th> for this column. */
  header: (expanded: boolean) => React.ReactNode;
  /** Full <td> for this column on the given row, or null to emit nothing. */
  cell: (row: R) => React.ReactNode;
  /** Optional sort key. Pair with `sortValue`. When both are set the header
   * becomes click-to-sort and ColumnTable handles the sort state + arrow. */
  sortKey?: string;
  /** Value extractor used for ordering. Return null/undefined to sort that
   * row last regardless of direction. */
  sortValue?: (row: R) => number | string | null | undefined;
}

/**
 * A "Ledger" — the canonical column-function-driven table used across the
 * app (Trades Summary, VS/CC Trade History, Open Vertical Spreads). One
 * `TableColumn<R>` list owns header + cell for every row; rows are tone-keyed
 * by their diagnostic `code`.
 */
export interface LedgerProps<R> {
  columns: TableColumn<R>[];
  rows: R[];
  expanded: boolean;
  tableClassName?: string;
  headRowClassName?: string;
  /** Page-supplied <tr> wrapper; receives the row and its rendered cells. */
  renderRow: (row: R, cells: React.ReactNode, index: number) => React.ReactNode;
  /**
   * Extra markup appended after the column-driven rows, inside the same
   * <tbody> (same column grid). Used to keep a not-yet-converted sub-section
   * (e.g. the Trades Summary "Closed" block) intact and aligned.
   */
  footer?: React.ReactNode;
  /** Initial sort column id (matches a column's sortKey). */
  defaultSortKey?: string;
  /** Initial direction. Default true = ascending (oldest-first for dates). */
  defaultSortAsc?: boolean;
  /**
   * For hierarchical ledgers (Trades Summary, Trade History) returning true
   * marks a row as a sortable "leaf". Sorting then happens within each
   * contiguous run of sortable rows; non-sortable rows (group headers,
   * totals) stay in place as run boundaries.
   *
   * Default: every row is sortable, producing a single flat sort.
   */
  isSortable?: (row: R) => boolean;
}

export function ColumnTable<R>({
  columns,
  rows,
  expanded,
  tableClassName,
  headRowClassName,
  renderRow,
  footer,
  defaultSortKey,
  defaultSortAsc,
  isSortable,
}: LedgerProps<R>) {
  const { effectiveIsSudo } = useAuth();
  // Diagnostic "Code" column: only when the effective (viewed) user is sudo.
  // Hides for regular users and for admins impersonating a non-sudo user.
  const cols = columns.filter(
    (c) => (expanded || !c.expandedOnly) && (effectiveIsSudo || c.id !== "code"),
  );
  const [sortKey, setSortKey] = React.useState<string | null>(defaultSortKey ?? null);
  const [sortAsc, setSortAsc] = React.useState<boolean>(defaultSortAsc ?? true);

  const toggle = (key: string) => {
    if (sortKey === key) setSortAsc((a) => !a);
    else { setSortKey(key); setSortAsc(true); }
  };

  const sortCol = cols.find((c) => c.sortKey === sortKey && c.sortValue);
  const sortedRows = React.useMemo(() => {
    if (!sortCol?.sortValue) return rows;
    const getVal = sortCol.sortValue;
    const compare = (a: R, b: R) => {
      const va = getVal(a);
      const vb = getVal(b);
      const na = va == null;
      const nb = vb == null;
      if (na && nb) return 0;
      if (na) return 1;          // nulls always last
      if (nb) return -1;
      const cmp = typeof va === "string"
        ? va.localeCompare(vb as string)
        : (va as number) - (vb as number);
      return sortAsc ? cmp : -cmp;
    };
    if (!isSortable) {
      return [...rows].sort(compare);
    }
    // Sort each contiguous run of sortable rows independently; non-sortable
    // rows stay in place and act as run boundaries.
    const out: R[] = new Array(rows.length);
    let i = 0;
    while (i < rows.length) {
      if (!isSortable(rows[i])) {
        out[i] = rows[i];
        i += 1;
        continue;
      }
      let j = i;
      while (j < rows.length && isSortable(rows[j])) j += 1;
      const run = rows.slice(i, j).sort(compare);
      for (let k = 0; k < run.length; k += 1) out[i + k] = run[k];
      i = j;
    }
    return out;
  }, [rows, sortCol, sortAsc, isSortable]);

  // Wrap each header <th>: add cursor + onClick when the column is sortable,
  // and append a sort-direction arrow when this column is the active sort.
  const renderHeader = (c: TableColumn<R>) => {
    const el = c.header(expanded);
    if (!c.sortKey || !c.sortValue || !React.isValidElement(el) || el.type !== "th") {
      return <React.Fragment key={c.id}>{el}</React.Fragment>;
    }
    const props = el.props as React.ThHTMLAttributes<HTMLTableCellElement> & { children?: React.ReactNode };
    const active = sortKey === c.sortKey;
    const arrow = active ? (sortAsc ? " ▲" : " ▼") : "";
    return React.cloneElement(el, {
      key: c.id,
      onClick: () => toggle(c.sortKey!),
      className: `${props.className ?? ""} cursor-pointer hover:text-cyan select-none`.trim(),
      children: (
        <>
          {props.children}
          {active && <span className="text-amber">{arrow}</span>}
        </>
      ),
    } as React.HTMLAttributes<HTMLTableCellElement>);
  };

  return (
    <table className={tableClassName}>
      <thead>
        <tr className={headRowClassName}>
          {cols.map((c) => renderHeader(c))}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((r, i) =>
          renderRow(
            r,
            cols.map((c) => (
              <React.Fragment key={c.id}>{c.cell(r)}</React.Fragment>
            )),
            i,
          ),
        )}
        {footer}
      </tbody>
    </table>
  );
}
